import { OUTLINE_BUTTON } from "@/components/site/styles";
import { ANALYTICS_DEFAULT_TEXT, ANALYTICS_TEXT } from "@/lib/consent";
import { actionProps, scopeProps } from "@/lib/controlAnalytics";
import { ROUTES } from "@/lib/routes";

/**
 * The analytics question, small, in a corner: on a phone it sits over
 * the tab bar. It blocks nothing; until it is answered nothing is
 * recorded. Both answers look the same, so no is as easy as yes. Where
 * analytics is on until the person says no (`byDefault`), it says so, and
 * its answers are to turn it off or to allow session recordings too.
 */
export default function AnalyticsBanner({
    byDefault = false,
    busy = false,
    onAllow,
    onDecline,
}: {
    byDefault?: boolean;
    busy?: boolean;
    onAllow: () => void;
    onDecline: () => void;
}) {
    return (
        <section
            {...scopeProps({ source: "AnalyticsBanner" })}
            role="region"
            aria-labelledby="analytics-banner-title"
            className="fixed bottom-5 left-5 z-40 flex w-[min(400px,calc(100vw-40px))] animate-fade-in flex-col gap-3 rounded-card border border-line bg-card p-4 text-fg shadow-pop max-[760px]:inset-x-4 max-[760px]:bottom-[calc(72px+env(safe-area-inset-bottom))] max-[760px]:w-auto"
        >
            <h2 id="analytics-banner-title" className="m-0 font-display text-[15px] font-bold">
                Analytics
            </h2>
            <p className="m-0 text-[12.5px] leading-[1.5] text-fg-muted">
                {byDefault ? ANALYTICS_DEFAULT_TEXT : ANALYTICS_TEXT}{" "}
                <a
                    {...actionProps("navigation_clicked", {
                        source: "AnalyticsBanner",
                        target: ROUTES.PRIVACY,
                    })}
                    href={`${ROUTES.PRIVACY}#cookies`}
                    target="_blank"
                    rel="noopener"
                    className="font-semibold text-brand-ink underline underline-offset-2"
                >
                    Privacy policy
                </a>
            </p>
            <div className="grid grid-cols-2 gap-2">
                <button
                    {...actionProps("analytics_banner_declined", { source: "AnalyticsBanner" })}
                    type="button"
                    disabled={busy}
                    onClick={onDecline}
                    className={`${OUTLINE_BUTTON} h-10 rounded-control text-[13px]`}
                >
                    {byDefault ? "Turn off" : "No thanks"}
                </button>
                <button
                    {...actionProps("analytics_banner_allowed", { source: "AnalyticsBanner" })}
                    type="button"
                    disabled={busy}
                    onClick={onAllow}
                    className={`${OUTLINE_BUTTON} h-10 rounded-control text-[13px]`}
                >
                    {byDefault ? "Allow recordings" : "Allow analytics"}
                </button>
            </div>
        </section>
    );
}
